/* Harper & Stone — the saved list.
 *
 * localStorage only, guest-only (settled #11). Nothing is sent anywhere and
 * nothing needs an account, which is the right trade for a list whose job is
 * "let me think about it overnight".
 *
 * What is stored is a list of handles, not rendered cards. A saved piece is
 * drawn by fetching the product's own page and reading its title, price and
 * lead photograph, so what the drawer shows is whatever the store says today
 * rather than whatever it said on the day the piece was saved.
 */

const KEY = 'hs:wishlist';

function read() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string') : [];
  } catch {
    /* Private mode, cleared data, or a value someone else wrote. An empty
     * list is always a safe answer; never let this throw into a click. */
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* Storage full or blocked. The in-page state is still correct for this
     * visit, so the heart still responds — it just will not survive a
     * reload, which is better than a dead button. */
  }
  document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list }, bubbles: true }));
}

function has(handle) {
  return read().includes(handle);
}

function toggle(handle) {
  const list = read();
  const i = list.indexOf(handle);
  if (i === -1) list.push(handle);
  else list.splice(i, 1);
  write(list);
  return i === -1;
}

/* Every heart on the page, wherever it is: the product page's button, and any
 * card that grows one later. Marked with aria-pressed, which is the honest
 * role for a toggle — aria-label swapping makes a screen reader announce a
 * different button rather than the same button in a different state. */
function paintToggles(root = document) {
  const list = read();
  for (const el of root.querySelectorAll('[data-wishlist-toggle]')) {
    const on = list.includes(el.dataset.handle);
    el.setAttribute('aria-pressed', String(on));
    el.dataset.saved = String(on);
  }
}

function paintCount() {
  const n = read().length;
  for (const el of document.querySelectorAll('[data-wishlist-count]')) {
    el.textContent = String(n);
    el.hidden = n === 0;
  }
  for (const el of document.querySelectorAll('[data-wishlist-title-count]')) {
    el.textContent = n ? `(${n})` : '';
  }
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-wishlist-toggle]');
  if (!btn) return;
  e.preventDefault();
  const handle = btn.dataset.handle;
  if (!handle) return;
  toggle(handle);
  paintToggles();
  paintCount();
});

document.addEventListener('wishlist:change', () => {
  paintToggles();
  paintCount();
});

/* Another tab saved something. Keep this one honest. */
window.addEventListener('storage', (e) => {
  if (e.key !== KEY) return;
  paintToggles();
  paintCount();
});

class HsWishlistDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.body = this.querySelector('[data-wishlist-body]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    if (!this.dialog) return;

    for (const b of document.querySelectorAll('[data-wishlist-open]')) {
      b.addEventListener('click', () => this.open(b));
    }

    this.querySelector('[data-wishlist-close]')?.addEventListener('click', () => {
      this.dialog.close();
    });

    // A click on the backdrop closes. <dialog> reports those as clicks on
    // itself, so compare against the dialog's own box.
    this.dialog.addEventListener('click', (e) => {
      if (e.target !== this.dialog) return;
      const r = this.dialog.getBoundingClientRect();
      const inside =
        e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) this.dialog.close();
    });

    this.dialog.addEventListener('close', () => {
      document.body.style.removeProperty('overflow');
      this.opener?.focus();
      this.opener = null;
    });

    // Removing the last saved piece from inside the drawer has to empty the
    // drawer too, not just the count in the header.
    document.addEventListener('wishlist:change', () => {
      if (this.dialog.open) this.#fill();
    });
  }

  async open(opener) {
    this.opener = opener || null;
    if (!this.dialog.open) {
      this.dialog.showModal();
      document.body.style.overflow = 'hidden';
    }
    await this.#fill();
  }

  async #fill() {
    const list = read();

    for (const old of this.body.querySelectorAll('[data-wishlist-item]')) old.remove();
    this.empty.hidden = list.length > 0;
    if (!list.length) {
      this.dialog.removeAttribute('aria-busy');
      return;
    }

    this.dialog.setAttribute('aria-busy', 'true');

    /* Each card is lifted from its own product page. `Promise.all` so six
     * saved pieces are six parallel requests rather than six round trips,
     * and a failed one is skipped rather than emptying the drawer. */
    const cards = await Promise.all(
      list.map(async (handle) => {
        try {
          const res = await fetch(`/products/${encodeURIComponent(handle)}`);
          if (!res.ok) return null;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          /* The product's own card, not the first `.pcard` on the page —
           * that is the recommendations rail, so every saved piece came back
           * as whatever the rail happened to show first. A product page has
           * no card of itself, so one is built from the page's own title,
           * price and lead photograph. */
          const title = doc.querySelector('.pdp__title')?.textContent?.trim();
          if (!title) return null;
          const price = doc.querySelector('.pdp__price')?.textContent?.trim() || '';
          /* The gallery's lead photograph. `.pgal__image` is the class on
           * the <img> inside the zoom button; falling back to any image in
           * the track covers a product whose lead media is a video, where
           * the poster is what should be shown. */
          const img =
            doc.querySelector('.pgal__item--lead img') || doc.querySelector('.pgal__track img');
          return {
            handle,
            title,
            price,
            src: img?.getAttribute('src') || '',
            srcset: img?.getAttribute('srcset') || '',
            alt: img?.getAttribute('alt') || title,
          };
        } catch {
          return null;
        }
      }),
    );

    /* A handle that no longer resolves — the product was unpublished or
     * renamed — is dropped from the list rather than counted forever in a
     * badge against a row that cannot be drawn. */
    const dead = list.filter((h, i) => !cards[i]);
    if (dead.length) {
      write(list.filter((h) => !dead.includes(h)));
      return;
    }

    for (const entry of cards) {
      if (!entry) continue;
      const wrap = document.createElement('div');
      wrap.className = 'wdraw__item';
      wrap.dataset.wishlistItem = entry.handle;

      const link = document.createElement('a');
      link.className = 'wdraw__link';
      link.href = `/products/${entry.handle}`;

      const figure = document.createElement('div');
      figure.className = 'wdraw__media';
      if (entry.src) {
        const img = document.createElement('img');
        img.src = entry.src;
        if (entry.srcset) img.srcset = entry.srcset;
        img.alt = entry.alt;
        img.loading = 'lazy';
        figure.append(img);
      }

      const name = document.createElement('p');
      name.className = 'wdraw__name';
      name.textContent = entry.title;

      const cost = document.createElement('p');
      cost.className = 'wdraw__price numeric';
      cost.textContent = entry.price;

      /* Name and price share one cell. Appended straight to the link they
       * became the grid's third and fourth items, so the price fell into the
       * next row under the photograph and wrapped. */
      const text = document.createElement('div');
      text.className = 'wdraw__text';
      text.append(name, cost);

      link.append(figure, text);
      wrap.append(link);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'button-icon wdraw__remove';
      remove.dataset.wishlistToggle = '';
      remove.dataset.handle = entry.handle;
      remove.setAttribute('aria-label', this.dataset.removeLabel || 'Remove from saved');
      remove.innerHTML =
        '<svg class="hs-icon hs-icon--sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      wrap.append(remove);

      this.body.append(wrap);
    }

    this.dialog.removeAttribute('aria-busy');
  }
}

if (!customElements.get('hs-wishlist-drawer')) {
  customElements.define('hs-wishlist-drawer', HsWishlistDrawer);
}

paintToggles();
paintCount();
